import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import InvoiceList from './InvoiceList';
import PaymentHistory from './PaymentHistory';
import InvoiceDetail from './InvoiceDetail';
import PaymentPlanCard from './PaymentPlanCard';
import BillingStats from './BillingStats';
import OverdueAlerts from './OverdueAlerts';
import CreateInvoice from './CreateInvoice';
import './BillingDashboard.css';

interface PaymentPlan {
  id: string;
  plan_type: string;
  plan_status: string;
  billing_cycle: string;
  start_date: string;
  end_date: string;
  total_amount: number;
  monthly_amount?: number;
  installment_count?: number;
  installment_number?: number;
  next_billing_date?: string;
  student_id: string;
  student_name?: string;
  parent_name?: string;
}

const AdminBillingDashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'overview' | 'invoices' | 'payment-plans' | 'payments' | 'reporting'>('overview');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'month' | 'quarter' | 'year' | 'all'>('month');
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [createInvoiceFromPlan, setCreateInvoiceFromPlan] = useState<string | null>(null);
  const [invoiceListRefreshTrigger, setInvoiceListRefreshTrigger] = useState(0);

  // Debug: Log state changes
  useEffect(() => {
    console.log('🔍 showCreateInvoice state changed to:', showCreateInvoice);
  }, [showCreateInvoice]);

  useEffect(() => {
    fetchPaymentPlans();
  }, []);

  const fetchPaymentPlans = async () => {
    try {
      setLoading(true);
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/payment-plans/active-for-billing`,
        { headers }
      );
      setPaymentPlans(response.data || []);
    } catch (err: any) {
      console.error('Error fetching payment plans:', err);
      setError(err.response?.data?.detail || 'Failed to load payment plans');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceClick = (invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
  };

  const handleViewInvoice = async (invoiceId: string) => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Use pdf-url endpoint (similar to consent forms workflow)
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/pdf-url`,
        { headers }
      );
      
      if (response.data.success && response.data.pdf_url) {
        window.open(response.data.pdf_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to load PDF. Please try again.');
      }
    } catch (err: any) {
      console.error('Error viewing invoice:', err);
      alert(err.response?.data?.detail || 'Failed to view invoice');
    }
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Use pdf-url endpoint (similar to consent forms workflow)
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/pdf-url`,
        { headers }
      );
      
      if (response.data.success && response.data.pdf_url) {
        window.open(response.data.pdf_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to load PDF. Please try again.');
      }
    } catch (err: any) {
      console.error('Error downloading invoice:', err);
      alert(err.response?.data?.detail || 'Failed to download invoice');
    }
  };

  const handleSendInvoiceEmail = async (invoiceId: string) => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/send`,
        {},
        { headers }
      );
      
      alert('Invoice sent successfully');
    } catch (err: any) {
      console.error('Error sending invoice:', err);
      alert(err.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleCreateInvoice = (planId?: string) => {
    console.log('🔍🔍🔍 handleCreateInvoice CALLED with planId:', planId);
    console.log('🔍🔍🔍 Current showCreateInvoice state:', showCreateInvoice);
    
    if (planId) {
      console.log('🔍🔍🔍 Setting createInvoiceFromPlan to:', planId);
      setCreateInvoiceFromPlan(planId);
    } else {
      console.log('🔍🔍🔍 Clearing createInvoiceFromPlan');
      setCreateInvoiceFromPlan(null);
    }
    
    console.log('🔍🔍🔍 About to set showCreateInvoice to TRUE');
    setShowCreateInvoice(true);
    console.log('🔍🔍🔍 showCreateInvoice set to true (state update queued)');
  };

  const handleInvoiceCreated = () => {
    // Refresh payment plans and invoice list
    fetchPaymentPlans();
    setShowCreateInvoice(false);
    setCreateInvoiceFromPlan(null);
    // Trigger invoice list refresh
    setInvoiceListRefreshTrigger(prev => prev + 1);
  };

  const handleRecordPayment = (invoiceId: string) => {
    // TODO: Open payment recording modal
    console.log('Record payment for invoice:', invoiceId);
  };

  const handleCancelInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) {
      return;
    }
    
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}`,
        { headers }
      );
      
      alert('Invoice cancelled successfully');
      // Refresh invoice list
    } catch (err: any) {
      console.error('Error cancelling invoice:', err);
      alert(err.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    const warningMessage = '⚠️ WARNING: This will PERMANENTLY delete this invoice and all related records.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure you want to delete this invoice?';
    if (!window.confirm(warningMessage)) {
      return;
    }
    
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/hard-delete`,
        {},
        { headers }
      );
      
      alert('✅ Invoice deleted permanently');
      // Refresh invoice list
      setInvoiceListRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      console.error('❌ Error deleting invoice:', err);
      alert(err.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleStatusChange = async (invoiceId: string, newStatus: string) => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}`,
        { status: newStatus },
        { headers }
      );
      
      // Refresh invoice list
      setInvoiceListRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      console.error('❌ Error updating invoice status:', err);
      alert(err.response?.data?.detail || 'Failed to update invoice status');
    }
  };

  const handleSendReminders = async () => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // TODO: Implement bulk reminder email endpoint
      alert('Reminder emails sent');
    } catch (err: any) {
      console.error('Error sending reminders:', err);
      alert('Failed to send reminder emails');
    }
  };

  return (
    <div className="admin-billing-dashboard">
      <div className="billing-dashboard-header">
        <h2>Billing Management</h2>
        <p>Manage invoices, payment plans, and financial reporting</p>
      </div>

      {/* Navigation */}
      <div className="billing-dashboard-nav">
        <button
          className={`billing-nav-tab ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveSection('invoices')}
        >
          📄 Invoices
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'payment-plans' ? 'active' : ''}`}
          onClick={() => setActiveSection('payment-plans')}
        >
          💳 Payment Plans
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveSection('payments')}
        >
          💰 Payments
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'reporting' ? 'active' : ''}`}
          onClick={() => setActiveSection('reporting')}
        >
          📈 Reporting
        </button>
      </div>

      {/* Content */}
      <div className="billing-dashboard-content">
        {activeSection === 'overview' && (
          <div className="billing-overview-section">
            <BillingStats dateRange={dateRange} onDateRangeChange={setDateRange} />
            
            <OverdueAlerts
              onViewOverdue={() => setActiveSection('invoices')}
              onSendReminders={handleSendReminders}
              isAdmin={true}
            />

            <div className="billing-overview-actions">
              <button 
                className="btn btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔍 Button clicked! Calling handleCreateInvoice...');
                  handleCreateInvoice();
                }}
              >
                Create Invoice
              </button>
            </div>

            <div className="billing-overview-recent">
              <h3>Recent Invoices</h3>
              <InvoiceList
                onInvoiceClick={handleInvoiceClick}
                onViewInvoice={handleViewInvoice}
                onDownloadInvoice={handleDownloadInvoice}
                onDeleteInvoice={handleDeleteInvoice}
                onStatusChange={handleStatusChange}
                isAdmin={true}
                refreshTrigger={invoiceListRefreshTrigger}
              />
            </div>
          </div>
        )}

        {activeSection === 'invoices' && (
          <div className="billing-invoices-section">
            <div className="section-header">
              <h3>Invoice Management</h3>
              <button 
                className="btn btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔍 Button clicked! Calling handleCreateInvoice...');
                  handleCreateInvoice();
                }}
              >
                Create Invoice
              </button>
            </div>
            <InvoiceList
              onInvoiceClick={handleInvoiceClick}
              onDownloadInvoice={handleDownloadInvoice}
              onDeleteInvoice={handleDeleteInvoice}
              onStatusChange={handleStatusChange}
              isAdmin={true}
              refreshTrigger={invoiceListRefreshTrigger}
            />
          </div>
        )}

        {activeSection === 'payment-plans' && (
          <div className="billing-payment-plans-section">
            <div className="section-header">
              <h3>Payment Plans</h3>
            </div>
            {loading ? (
              <div className="loading">Loading payment plans...</div>
            ) : error ? (
              <div className="error-message">Error: {error}</div>
            ) : paymentPlans.length === 0 ? (
              <div className="empty-state">
                <p>No active payment plans</p>
              </div>
            ) : (
              <div className="payment-plans-grid">
                {paymentPlans.map(plan => (
                  <PaymentPlanCard
                    key={plan.id}
                    plan={plan}
                    onView={(planId) => console.log('View plan:', planId)}
                    onCancel={(planId) => console.log('Cancel plan:', planId)}
                    onCreateInvoice={(planId) => handleCreateInvoice(planId)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeSection === 'payments' && (
          <div className="billing-payments-section">
            <div className="section-header">
              <h3>Payment Tracking</h3>
            </div>
            <PaymentHistory />
          </div>
        )}

        {activeSection === 'reporting' && (
          <div className="billing-reporting-section">
            <div className="section-header">
              <h3>Financial Reporting</h3>
            </div>
            <BillingStats dateRange={dateRange} onDateRangeChange={setDateRange} />
            {/* TODO: Add revenue charts and detailed reports */}
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {(() => {
        console.log('🔍🔍🔍 Rendering check - showCreateInvoice:', showCreateInvoice);
        return showCreateInvoice ? (
          <CreateInvoice
            show={showCreateInvoice}
            onClose={() => {
              console.log('🔍 CreateInvoice onClose called');
              setShowCreateInvoice(false);
              setCreateInvoiceFromPlan(null);
            }}
            onSuccess={handleInvoiceCreated}
            paymentPlanId={createInvoiceFromPlan || undefined}
          />
        ) : null;
      })()}

      {/* Invoice Detail Modal */}
      {selectedInvoiceId && (
        <InvoiceDetail
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onDownload={handleDownloadInvoice}
          onSendEmail={handleSendInvoiceEmail}
          onRecordPayment={handleRecordPayment}
          onCancel={handleCancelInvoice}
          isAdmin={true}
        />
      )}
    </div>
  );
};

export default AdminBillingDashboard;

